import React, { useMemo } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Box, Typography } from "@mui/material";
import { nFormatter } from "../../../Helpers";
import { currencySymbolHtml } from "../../../Components/common/CurrencySymbol";
import {
  applyEnterpriseChartTheme,
  HC_CONTAINER_PROPS,
} from "../../ExDashboard/components/chartTheme";
import { MD } from "./mdTheme";
import { MdWidgetCard } from "./MdCardShell";
import { MdCurrencyHint, MdMoneyValue } from "./MdMoneyValue";

/** Bayanat DD: max bar = green, last month = amber, else blue */
const barColor = (value, index, values) => {
  const max = Math.max(...values, 0);
  const last = values.length - 1;
  if (value === max && max > 0) return MD.good;
  if (index === last) return MD.chart[1];
  return MD.chart[0];
};

const MonthlyNetChartCard = ({
  translate,
  loading,
  data = {},
  loginUserCurrency,
}) => {
  const categories = useMemo(
    () => Object.keys(data || {}).reverse(),
    [data],
  );
  const values = useMemo(
    () => Object.values(data || {}).reverse().map((v) => Number(v) || 0),
    [data],
  );
  const yearTotal = useMemo(
    () => values.reduce((sum, v) => sum + v, 0),
    [values],
  );

  const options = useMemo(() => {
    if (!categories.length) return null;

    return applyEnterpriseChartTheme({
      chart: {
        type: "column",
        height: 320,
        backgroundColor: "transparent",
        spacing: [12, 8, 8, 8],
      },
      title: { text: null },
      xAxis: {
        categories,
        title: { text: null },
        lineColor: MD.line,
        tickLength: 0,
        labels: {
          style: {
            color: MD.muted,
            fontSize: "11px",
            fontWeight: "500",
          },
        },
      },
      yAxis: {
        title: { text: null },
        gridLineColor: MD.line2,
        gridLineWidth: 1,
        labels: {
          style: { color: MD.muted, fontSize: "11px", fontWeight: "500" },
          formatter() {
            return nFormatter(this.value, 1);
          },
        },
      },
      tooltip: {
        formatter() {
          return (
            "<strong>" +
            this.key +
            "</strong><br/><strong>" +
            Number(this.y).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }) +
            currencySymbolHtml(loginUserCurrency) +
            "</strong>"
          );
        },
      },
      plotOptions: {
        column: {
          borderWidth: 0,
          borderRadius: 4,
          maxPointWidth: 44,
          pointPadding: 0.12,
          groupPadding: 0.08,
          dataLabels: {
            enabled: true,
            crop: false,
            overflow: "allow",
            useHTML: true,
            style: {
              color: MD.fg,
              fontSize: "10px",
              fontWeight: "600",
              textOutline: "none",
            },
            formatter() {
              return this.y
                ? nFormatter(this.y, 1) + currencySymbolHtml(loginUserCurrency)
                : "";
            },
          },
        },
      },
      series: [
        {
          name: "Net",
          showInLegend: false,
          data: values.map((y, i) => ({
            y,
            color: barColor(y, i, values),
          })),
        },
      ],
      credits: { enabled: false },
      legend: { enabled: false },
    });
  }, [categories, values, loginUserCurrency]);

  return (
    <MdWidgetCard
      loading={loading}
      eyebrow={
        <>
          {translate("monthlySalesNetAmount") || "Monthly Sales Net Amount"}
          <MdCurrencyHint currency={loginUserCurrency} />
        </>
      }
      title={translate("last12Months")}
      headValue={
        <Box sx={{ ml: "auto", textAlign: "right" }}>
          <MdMoneyValue
            amount={yearTotal}
            currency={loginUserCurrency}
            fontSize={22}
            showTooltip={false}
            sx={{ justifyContent: "flex-end" }}
          />
          <Typography component="span" sx={{ color: MD.muted, fontSize: 12 }}>
            {translate("twelveMonthTotal")}
          </Typography>
        </Box>
      }
    >
      <Box sx={{ height: 320, width: "100%", mt: 1, overflow: "hidden" }}>
        {options ? (
          <HighchartsReact
            highcharts={Highcharts}
            options={options}
            containerProps={{
              ...HC_CONTAINER_PROPS,
              style: { height: 320, width: "100%", overflow: "hidden" },
            }}
            immutable
          />
        ) : null}
      </Box>
    </MdWidgetCard>
  );
};

export default MonthlyNetChartCard;
